import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
    Plus, 
    Edit, 
    Trash2, 
    UsersRound, 
    User, 
    Layers,
    GraduationCap,
    Award,
    TrendingUp,
    ChevronRight,
    BarChart3,
    CheckCircle,
    Clock,
    Circle,
    Eye,
    Settings,
    BookOpen
} from 'lucide-react';

const statusConfig = {
    completed: { label: 'Completed', color: 'bg-emerald-500/10 text-emerald-500', icon: CheckCircle },
    in_progress: { label: 'In Progress', color: 'bg-blue-500/10 text-blue-500', icon: Clock },
    not_started: { label: 'Not Started', color: 'bg-muted text-muted-foreground', icon: Circle }
};

const Batches = () => {
    const { api, user: currentUser } = useAuth();
    const [batches, setBatches] = useState([]);
    const [users, setUsers] = useState([]);
    const [modules, setModules] = useState([]);
    const [curricula, setCurricula] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingBatch, setEditingBatch] = useState(null);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [batchProgress, setBatchProgress] = useState(null);
    const [progressLoading, setProgressLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        user_ids: [],
        curriculum_ids: [],
        module_ids: [],
        certificate_enabled: false,
        certificate_template: 'default'
    });

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedBatch) {
            fetchBatchProgress(selectedBatch.id);
        }
    }, [selectedBatch]);

    const fetchData = async () => {
        try {
            const [batchesRes, usersRes, modulesRes, curriculaRes] = await Promise.all([
                api.get('/batches'),
                api.get('/users'),
                api.get('/modules'),
                api.get('/curriculum')
            ]);
            setBatches(batchesRes.data);
            setUsers(usersRes.data.filter(u => u.role === 'user'));
            setModules(modulesRes.data);
            setCurricula(curriculaRes.data.filter(c => c.status === 'published' || c.status === 'pending_review'));
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const fetchBatchProgress = async (batchId) => {
        setProgressLoading(true);
        try {
            const response = await api.get(`/batches/${batchId}/progress`);
            setBatchProgress(response.data);
        } catch (error) {
            console.error('Failed to fetch progress:', error);
            setBatchProgress(null);
        } finally {
            setProgressLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingBatch) {
                await api.put(`/batches/${editingBatch.id}`, formData);
                toast.success('Batch updated');
            } else {
                await api.post('/batches', formData);
                toast.success('Batch created');
            }
            fetchData();
            resetForm();
        } catch (error) {
            toast.error('Failed to save batch');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Delete this batch?')) return;
        try {
            await api.delete(`/batches/${id}`);
            toast.success('Batch deleted');
            if (selectedBatch?.id === id) {
                setSelectedBatch(null);
                setBatchProgress(null);
            }
            fetchData();
        } catch (error) {
            toast.error('Failed to delete batch');
        }
    };

    const toggleItem = (field, itemId) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].includes(itemId)
                ? prev[field].filter(id => id !== itemId)
                : [...prev[field], itemId]
        }));
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            user_ids: [],
            curriculum_ids: [],
            module_ids: [],
            certificate_enabled: false,
            certificate_template: 'default'
        });
        setEditingBatch(null);
        setDialogOpen(false);
    };

    const openEdit = (batch) => {
        setEditingBatch(batch);
        setFormData({
            name: batch.name,
            description: batch.description || '',
            user_ids: batch.user_ids || [],
            curriculum_ids: batch.curriculum_ids || [],
            module_ids: batch.module_ids || [],
            certificate_enabled: batch.certificate_enabled || false,
            certificate_template: batch.certificate_template || 'default'
        });
        setDialogOpen(true);
    };

    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'trainer';

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="batches-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <UsersRound className="h-8 w-8 text-primary" />
                            Team Batches
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Manage teams, assign curricula, and track progress
                        </p>
                    </div>
                    {isAdmin && (
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="btn-primary gap-2" data-testid="create-batch-btn">
                                    <Plus className="h-4 w-4" />
                                    Create Batch
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
                                <DialogHeader>
                                    <DialogTitle>{editingBatch ? 'Edit Batch' : 'Create Batch'}</DialogTitle>
                                    <DialogDescription>
                                        Create a team batch and assign curricula for group learning
                                    </DialogDescription>
                                </DialogHeader>
                                <ScrollArea className="max-h-[70vh] pr-4">
                                    <form onSubmit={handleSubmit} className="space-y-6 pt-2">
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div className="space-y-2">
                                                <Label htmlFor="name">Batch Name *</Label>
                                                <Input
                                                    id="name"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                    placeholder="e.g., Q1 Sales Team"
                                                    required
                                                    data-testid="batch-name-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="description">Description</Label>
                                                <Input
                                                    id="description"
                                                    value={formData.description}
                                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                    placeholder="Brief description"
                                                />
                                            </div>
                                        </div>

                                        {/* Certificate Settings */}
                                        <div className="p-4 rounded-xl border bg-muted/30 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Award className="h-5 w-5 text-amber-500" />
                                                    <div>
                                                        <Label>Issue Certificates</Label>
                                                        <p className="text-xs text-muted-foreground">
                                                            Award certificates upon curriculum completion
                                                        </p>
                                                    </div>
                                                </div>
                                                <Switch
                                                    checked={formData.certificate_enabled}
                                                    onCheckedChange={(checked) => setFormData({ ...formData, certificate_enabled: checked })}
                                                    data-testid="certificate-toggle"
                                                />
                                            </div>
                                        </div>

                                        {/* Team Members */}
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                <User className="h-4 w-4" />
                                                Team Members ({formData.user_ids.length} selected)
                                            </Label>
                                            <div className="border rounded-lg p-4 max-h-40 overflow-y-auto space-y-2 bg-background">
                                                {users.map((user) => (
                                                    <div key={user.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                                                        <Checkbox
                                                            id={`user-${user.id}`}
                                                            checked={formData.user_ids.includes(user.id)}
                                                            onCheckedChange={() => toggleItem('user_ids', user.id)}
                                                        />
                                                        <label htmlFor={`user-${user.id}`} className="flex-1 text-sm cursor-pointer">
                                                            <span className="font-medium">{user.name}</span>
                                                            <span className="text-muted-foreground ml-2 text-xs">{user.email}</span>
                                                        </label>
                                                    </div>
                                                ))}
                                                {users.length === 0 && (
                                                    <p className="text-sm text-muted-foreground text-center py-4">No trainees available</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Curricula Assignment */}
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                <GraduationCap className="h-4 w-4" />
                                                Assign Curricula ({formData.curriculum_ids.length} selected)
                                            </Label>
                                            <div className="border rounded-lg p-4 max-h-40 overflow-y-auto space-y-2 bg-background">
                                                {curricula.map((curriculum) => (
                                                    <div key={curriculum.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                                                        <Checkbox
                                                            id={`curriculum-${curriculum.id}`}
                                                            checked={formData.curriculum_ids.includes(curriculum.id)}
                                                            onCheckedChange={() => toggleItem('curriculum_ids', curriculum.id)}
                                                        />
                                                        <label htmlFor={`curriculum-${curriculum.id}`} className="flex-1 text-sm cursor-pointer">
                                                            <span className="font-medium">{curriculum.title}</span>
                                                            <Badge variant="outline" className="ml-2 text-xs">
                                                                {curriculum.status}
                                                            </Badge>
                                                        </label>
                                                    </div>
                                                ))}
                                                {curricula.length === 0 && (
                                                    <p className="text-sm text-muted-foreground text-center py-4">No curricula available</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Modules Assignment */}
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                <Layers className="h-4 w-4" />
                                                Assign Modules ({formData.module_ids.length} selected)
                                            </Label>
                                            <div className="border rounded-lg p-4 max-h-40 overflow-y-auto space-y-2 bg-background">
                                                {modules.map((module) => (
                                                    <div key={module.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                                                        <Checkbox
                                                            id={`module-${module.id}`}
                                                            checked={formData.module_ids.includes(module.id)}
                                                            onCheckedChange={() => toggleItem('module_ids', module.id)}
                                                        />
                                                        <label htmlFor={`module-${module.id}`} className="flex-1 text-sm cursor-pointer">
                                                            {module.name}
                                                        </label>
                                                    </div>
                                                ))}
                                                {modules.length === 0 && (
                                                    <p className="text-sm text-muted-foreground text-center py-4">No modules available</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex gap-3 pt-4 sticky bottom-0 bg-background pb-2">
                                            <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                                                Cancel
                                            </Button>
                                            <Button type="submit" className="flex-1" data-testid="save-batch-btn">
                                                {editingBatch ? 'Update Batch' : 'Create Batch'}
                                            </Button>
                                        </div>
                                    </form>
                                </ScrollArea>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>

                {/* Main Content */}
                <div className="grid gap-6 lg:grid-cols-3">
                    {/* Batches List */}
                    <div className="lg:col-span-1 space-y-4">
                        {batches.map((batch) => (
                            <Card
                                key={batch.id}
                                className={`cursor-pointer transition-all hover:shadow-lg ${
                                    selectedBatch?.id === batch.id ? 'border-primary shadow-lg' : ''
                                }`}
                                onClick={() => setSelectedBatch(batch)}
                                data-testid={`batch-card-${batch.id}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20">
                                                <UsersRound className="h-5 w-5 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold">{batch.name}</h3>
                                                <p className="text-xs text-muted-foreground">
                                                    {batch.user_ids?.length || 0} members
                                                </p>
                                            </div>
                                        </div>
                                        {batch.certificate_enabled && (
                                            <Badge variant="outline" className="text-amber-500 border-amber-500/30">
                                                <Award className="h-3 w-3 mr-1" />
                                                Cert
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <GraduationCap className="h-3 w-3" />
                                            {batch.curriculum_ids?.length || 0} curricula
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Layers className="h-3 w-3" />
                                            {batch.module_ids?.length || 0} modules
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                        {batches.length === 0 && (
                            <Card>
                                <CardContent className="flex flex-col items-center justify-center py-12">
                                    <UsersRound className="h-12 w-12 text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">No batches found</p>
                                    <p className="text-xs text-muted-foreground mt-1">Create your first batch!</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Batch Detail & Progress */}
                    <div className="lg:col-span-2">
                        {selectedBatch ? (
                            <Card>
                                <CardHeader className="border-b">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <CardTitle className="flex items-center gap-2">
                                                <UsersRound className="h-5 w-5 text-primary" />
                                                {selectedBatch.name}
                                            </CardTitle>
                                            {selectedBatch.description && (
                                                <CardDescription>{selectedBatch.description}</CardDescription>
                                            )}
                                        </div>
                                        {isAdmin && (
                                            <div className="flex gap-2">
                                                <Button variant="outline" size="sm" onClick={() => openEdit(selectedBatch)}>
                                                    <Edit className="h-4 w-4 mr-1" />
                                                    Edit
                                                </Button>
                                                <Button variant="outline" size="sm" className="text-destructive" onClick={() => handleDelete(selectedBatch.id)}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <Tabs defaultValue="progress">
                                        <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
                                            <TabsTrigger value="progress" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                                                <BarChart3 className="h-4 w-4 mr-2" />
                                                Progress
                                            </TabsTrigger>
                                            <TabsTrigger value="members" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                                                <User className="h-4 w-4 mr-2" />
                                                Members
                                            </TabsTrigger>
                                            <TabsTrigger value="content" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                                                <BookOpen className="h-4 w-4 mr-2" />
                                                Content
                                            </TabsTrigger>
                                        </TabsList>

                                        {/* Progress Tab */}
                                        <TabsContent value="progress" className="p-4 space-y-4">
                                            {progressLoading ? (
                                                <div className="flex items-center justify-center py-12">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                                                </div>
                                            ) : batchProgress ? (
                                                <>
                                                    {/* Summary Stats */}
                                                    <div className="grid grid-cols-4 gap-4">
                                                        <div className="p-4 rounded-xl bg-primary/5 text-center">
                                                            <p className="text-2xl font-bold text-primary">{batchProgress.summary.average_progress}%</p>
                                                            <p className="text-xs text-muted-foreground">Avg Progress</p>
                                                        </div>
                                                        <div className="p-4 rounded-xl bg-emerald-500/5 text-center">
                                                            <p className="text-2xl font-bold text-emerald-500">{batchProgress.summary.completed_users}</p>
                                                            <p className="text-xs text-muted-foreground">Completed</p>
                                                        </div>
                                                        <div className="p-4 rounded-xl bg-muted/50 text-center">
                                                            <p className="text-2xl font-bold">{batchProgress.summary.total_users}</p>
                                                            <p className="text-xs text-muted-foreground">Total Users</p>
                                                        </div>
                                                        <div className="p-4 rounded-xl bg-muted/50 text-center">
                                                            <p className="text-2xl font-bold">{batchProgress.summary.total_topics}</p>
                                                            <p className="text-xs text-muted-foreground">Topics</p>
                                                        </div>
                                                    </div>

                                                    {/* User Progress List */}
                                                    <ScrollArea className="h-[300px]">
                                                        <div className="space-y-3">
                                                            {batchProgress.users.map((user, idx) => {
                                                                const StatusIcon = statusConfig[user.status]?.icon || Circle;
                                                                return (
                                                                    <div key={user.user_id} className="p-4 rounded-xl border bg-card">
                                                                        <div className="flex items-center justify-between mb-2">
                                                                            <div className="flex items-center gap-3">
                                                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                                                                    idx < 3 ? 'bg-amber-500/10 text-amber-500' : 'bg-muted'
                                                                                }`}>
                                                                                    {idx + 1}
                                                                                </div>
                                                                                <div>
                                                                                    <p className="font-medium">{user.name}</p>
                                                                                    <p className="text-xs text-muted-foreground">{user.email}</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className="text-right">
                                                                                <Badge variant="outline" className={statusConfig[user.status]?.color}>
                                                                                    <StatusIcon className="h-3 w-3 mr-1" />
                                                                                    {statusConfig[user.status]?.label}
                                                                                </Badge>
                                                                                {user.average_score !== null && (
                                                                                    <p className="text-xs text-muted-foreground mt-1">
                                                                                        Avg Score: {user.average_score}%
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="space-y-1">
                                                                            <div className="flex justify-between text-xs">
                                                                                <span>{user.completed_topics}/{user.total_topics} topics</span>
                                                                                <span className="font-medium">{user.progress_percentage}%</span>
                                                                            </div>
                                                                            <Progress value={user.progress_percentage} className={`h-2 ${
                                                                                user.status === 'completed' ? '[&>div]:bg-emerald-500' : ''
                                                                            }`} />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                            {batchProgress.users.length === 0 && (
                                                                <div className="text-center py-8 text-muted-foreground">
                                                                    No members in this batch
                                                                </div>
                                                            )}
                                                        </div>
                                                    </ScrollArea>
                                                </>
                                            ) : (
                                                <div className="text-center py-12 text-muted-foreground">
                                                    Unable to load progress data
                                                </div>
                                            )}
                                        </TabsContent>

                                        {/* Members Tab */}
                                        <TabsContent value="members" className="p-4">
                                            <div className="space-y-2">
                                                {selectedBatch.user_ids?.map((userId) => {
                                                    const user = users.find(u => u.id === userId);
                                                    return user ? (
                                                        <div key={userId} className="flex items-center gap-3 p-3 rounded-lg border">
                                                            <div className="p-2 rounded-full bg-primary/10">
                                                                <User className="h-4 w-4 text-primary" />
                                                            </div>
                                                            <div>
                                                                <p className="font-medium">{user.name}</p>
                                                                <p className="text-xs text-muted-foreground">{user.email}</p>
                                                            </div>
                                                        </div>
                                                    ) : null;
                                                })}
                                                {(!selectedBatch.user_ids || selectedBatch.user_ids.length === 0) && (
                                                    <div className="text-center py-8 text-muted-foreground">
                                                        No members assigned
                                                    </div>
                                                )}
                                            </div>
                                        </TabsContent>

                                        {/* Content Tab */}
                                        <TabsContent value="content" className="p-4 space-y-4">
                                            <div>
                                                <h4 className="font-medium mb-2 flex items-center gap-2">
                                                    <GraduationCap className="h-4 w-4 text-primary" />
                                                    Assigned Curricula
                                                </h4>
                                                <div className="space-y-2">
                                                    {selectedBatch.curriculum_ids?.map((curriculumId) => {
                                                        const curriculum = curricula.find(c => c.id === curriculumId);
                                                        return curriculum ? (
                                                            <div key={curriculumId} className="flex items-center gap-3 p-3 rounded-lg border">
                                                                <GraduationCap className="h-4 w-4 text-primary" />
                                                                <span>{curriculum.title}</span>
                                                                <Badge variant="outline" className="ml-auto">{curriculum.status}</Badge>
                                                            </div>
                                                        ) : null;
                                                    })}
                                                    {(!selectedBatch.curriculum_ids || selectedBatch.curriculum_ids.length === 0) && (
                                                        <p className="text-sm text-muted-foreground">No curricula assigned</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="font-medium mb-2 flex items-center gap-2">
                                                    <Layers className="h-4 w-4 text-primary" />
                                                    Assigned Modules
                                                </h4>
                                                <div className="space-y-2">
                                                    {selectedBatch.module_ids?.map((moduleId) => {
                                                        const module = modules.find(m => m.id === moduleId);
                                                        return module ? (
                                                            <div key={moduleId} className="flex items-center gap-3 p-3 rounded-lg border">
                                                                <Layers className="h-4 w-4 text-primary" />
                                                                <span>{module.name}</span>
                                                            </div>
                                                        ) : null;
                                                    })}
                                                    {(!selectedBatch.module_ids || selectedBatch.module_ids.length === 0) && (
                                                        <p className="text-sm text-muted-foreground">No modules assigned</p>
                                                    )}
                                                </div>
                                            </div>
                                        </TabsContent>
                                    </Tabs>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="h-full min-h-[400px] flex items-center justify-center">
                                <CardContent className="text-center">
                                    <UsersRound className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                                    <p className="text-muted-foreground">Select a batch to view details</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default Batches;
